import { useTranslation } from 'react-i18next';
import { Container } from 'react-bootstrap';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import { useState, useEffect, useRef } from 'react';
import Card from 'react-bootstrap/Card';
import api from '../api';
import { useNavigate } from 'react-router-dom';

function HomeScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const shuffleArray = (array) => {
    return [...array].sort(() => Math.random() - 0.5);
  }; // helper function to shuffle an array randomly

  const totalImages = 16; // total number of keycap set images available
  const [imageQueue, setImageQueue] = useState(
    shuffleArray([...Array(totalImages)].map((_, i) => i + 1))
  ); // state to manage the image queue (shuffled list of image numbers from 1 to 16)
  const [currentImage, setCurrentImage] = useState(imageQueue[0]); // the image currently being displayed
  const [nextIndex, setNextIndex] = useState(1); // index of the next image to be shown from the queue
  const [isTransitioning, setIsTransitioning] = useState(false); // whether the current image is fading out (used to trigger CSS transitions)
  const [fadeInNext, setFadeInNext] = useState(false); // whether the next image should start fading in
  const [topDesigns, setTopDesigns] = useState([]); // state to hold the top 10 most-liked community designs (fetched from backend)
  const scrollRef = useRef(null); // ref pointing to the scroll container that holds the top design cards

  useEffect(() => {
    // start an interval that runs every 4 seconds (4000 ms)
    const interval = setInterval(() => {
      setIsTransitioning(true); // mark that a transition (fade) is starting
      setFadeInNext(false); // reset fade-in state for the next image

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setFadeInNext(true); // trigger fade-in animation for next image
        });
      }); // use double requestAnimationFrame to ensure the fade-in starts *after* browser paints (If we would add the fade-in class immediately, the browser would batch the DOM changes and the transition won’t play, because it never sees the element in the “before” state.)

      // wait 800ms for the fade-in to complete before switching image
      setTimeout(() => {
        const newIndex = nextIndex + 1; // move to the next index
        const hasMore = newIndex < imageQueue.length; // check if there are more images left

        const newQueue = hasMore
          ? imageQueue
          : shuffleArray([...Array(totalImages)].map((_, i) => i + 1)); // if at end of queue, reshuffle for infinite looping; otherwise keep current queue. The new queue may start with the image shown now, one repeat in sixteen cycles: accepted (Oct 2026)

        setCurrentImage(imageQueue[nextIndex]); // set new image to be displayed
        setNextIndex(hasMore ? newIndex : 0); // update index, or restart the new queue at its first image (a reset to 1 skipped that image: the one shown now is the old queue's last; fixed Oct 2026)
        setImageQueue(newQueue); // update image queue (shuffled if looping)

        setIsTransitioning(false); // end transition state
        setFadeInNext(false); // reset fade-in state
      }, 800);
    }, 4000);

    return () => clearInterval(interval); // cleanup: clear interval when component unmounts or dependencies change
  }, [nextIndex, imageQueue]); // controls the automatic image slideshow (of the keycap sets) with smooth transitions

  useEffect(() => {
    const fetchTop = async () => {
      try {
        const { data } = await api.get('/api/designs/top');
        setTopDesigns(data);
      } catch (err) {
        console.error('Cannot load top designs', err);
      }
    };
    fetchTop();
  }, []); // fetch the 10 most-popular designs once on mount

  useEffect(() => {
    if (!scrollRef.current) return; // exit early if the scroll container doesn't exist yet

    const container = scrollRef.current; // reference to the scrollable container (holds the top design cards)

    const card = container.querySelector('.top-design-card'); // get the first card inside the container
    if (!card) return; // exit early if no card found

    const getStepSize = () => {
      const style = getComputedStyle(card); // get computed CSS styles of the card
      const marginLeft = parseFloat(style.marginLeft) || 0; // get left margin (default to 0 if invalid)
      const marginRight = parseFloat(style.marginRight) || 0; // get right margin (default to 0 if invalid)
      return card.offsetWidth + marginLeft + marginRight; // total scroll step = card width + margins
    };

    let step = getStepSize(); // the automatic scroll distance per step (matches the card width + margin)
    let prevWidth = window.innerWidth; // Track current window width for comparison

    // Recalculate step size only when the window width changes
    const handleResize = () => {
      const newWidth = window.innerWidth;
      if (newWidth !== prevWidth) {
        prevWidth = newWidth; // Update tracked width
        step = getStepSize(); // update step size with new card dimensions
        container.scrollTo({ left: 0, behavior: 'auto' }); // reset scroll position to the beginning
      }
    };

    window.addEventListener('resize', handleResize); // listen for screen resizes

    // set up a repeating scroll interval every 4 seconds
    const interval = setInterval(() => {
      if (container.scrollWidth <= container.clientWidth) return; // if content doesn't overflow, there's nothing to scroll

      const atEnd =
        container.scrollLeft + container.clientWidth + 1 >=
        container.scrollWidth; // check if we’ve reached the end of the scroll container

      if (atEnd) {
        container.scrollTo({ left: 0, behavior: 'smooth' }); // if at the end, scroll smoothly back to the beginning
      } else {
        container.scrollBy({ left: step, behavior: 'smooth' }); // otherwise, scroll to the next "step" (≈ next card)
      }
    }, 4000);

    return () => {
      clearInterval(interval); // clean up interval when component unmounts or topDesigns changes
      window.removeEventListener('resize', handleResize); // clean up the listener when effect ends
    };
  }, [topDesigns]); // auto-scroll through the community design cards every 4 s, loop when reaching the end

  return (
    <div>
      <title>{t('home-screen-helmet')}</title>

      {/* Floating particles in the background */}
      <div className="floating-particles">
        {/* Left side (0–30%) */}
        <span
          className="particle"
          style={{
            top: '10%',
            left: '5%',
            backgroundColor: '#fe4a49',
            width: '8px',
            height: '8px',
            animationDelay: '0s',
            opacity: 0.2,
          }}
        ></span>
        <span
          className="particle"
          style={{
            top: '20%',
            left: '20%',
            backgroundColor: '#ff5e66',
            width: '10px',
            height: '10px',
            animationDelay: '1s',
            opacity: 0.2,
          }}
        ></span>
        <span
          className="particle"
          style={{
            top: '30%',
            left: '12%',
            backgroundColor: '#ff7380',
            width: '6px',
            height: '6px',
            animationDelay: '2s',
            opacity: 0.2,
          }}
        ></span>
        <span
          className="particle"
          style={{
            top: '40%',
            left: '25%',
            backgroundColor: '#ff8a9c',
            width: '9px',
            height: '9px',
            animationDelay: '3s',
            opacity: 0.2,
          }}
        ></span>
        <span
          className="particle"
          style={{
            top: '50%',
            left: '18%',
            backgroundColor: '#ffa2b9',
            width: '7px',
            height: '7px',
            animationDelay: '4s',
            opacity: 0.2,
          }}
        ></span>
        <span
          className="particle"
          style={{
            top: '60%',
            left: '8%',
            backgroundColor: '#d28bd9',
            width: '8px',
            height: '8px',
            animationDelay: '2.5s',
            opacity: 0.2,
          }}
        ></span>
        <span
          className="particle"
          style={{
            top: '70%',
            left: '28%',
            backgroundColor: '#ba75e2',
            width: '10px',
            height: '10px',
            animationDelay: '1.5s',
            opacity: 0.2,
          }}
        ></span>
        <span
          className="particle"
          style={{
            top: '80%',
            left: '10%',
            backgroundColor: '#a855f7',
            width: '6px',
            height: '6px',
            animationDelay: '3.5s',
            opacity: 0.2,
          }}
        ></span>
        <span
          className="particle"
          style={{
            top: '15%',
            left: '2%',
            backgroundColor: '#c865e9',
            width: '9px',
            height: '9px',
            animationDelay: '2.2s',
            opacity: 0.2,
          }}
        ></span>
        <span
          className="particle"
          style={{
            top: '35%',
            left: '22%',
            backgroundColor: '#de78e2',
            width: '8px',
            height: '8px',
            animationDelay: '1.7s',
            opacity: 0.2,
          }}
        ></span>

        {/* Right side (70–100%) */}
        <span
          className="particle"
          style={{
            top: '5%',
            left: '75%',
            backgroundColor: '#ffb2c7',
            width: '5px',
            height: '5px',
            animationDelay: '2.8s',
            opacity: 0.2,
          }}
        ></span>
        <span
          className="particle"
          style={{
            top: '25%',
            left: '90%',
            backgroundColor: '#ff667a',
            width: '7px',
            height: '7px',
            animationDelay: '4.2s',
            opacity: 0.2,
          }}
        ></span>
        <span
          className="particle"
          style={{
            top: '35%',
            left: '72%',
            backgroundColor: '#ff7f96',
            width: '9px',
            height: '9px',
            animationDelay: '3s',
            opacity: 0.2,
          }}
        ></span>
        <span
          className="particle"
          style={{
            top: '45%',
            left: '85%',
            backgroundColor: '#f46aa7',
            width: '6px',
            height: '6px',
            animationDelay: '3.7s',
            opacity: 0.2,
          }}
        ></span>
        <span
          className="particle"
          style={{
            top: '55%',
            left: '95%',
            backgroundColor: '#e65ce2',
            width: '8px',
            height: '8px',
            animationDelay: '2.1s',
            opacity: 0.2,
          }}
        ></span>
        <span
          className="particle"
          style={{
            top: '65%',
            left: '73%',
            backgroundColor: '#c95fe6',
            width: '10px',
            height: '10px',
            animationDelay: '1.9s',
            opacity: 0.2,
          }}
        ></span>
        <span
          className="particle"
          style={{
            top: '75%',
            left: '80%',
            backgroundColor: '#ae4cf0',
            width: '7px',
            height: '7px',
            animationDelay: '3.2s',
            opacity: 0.2,
          }}
        ></span>
        <span
          className="particle"
          style={{
            top: '85%',
            left: '93%',
            backgroundColor: '#ff9fbb',
            width: '6px',
            height: '6px',
            animationDelay: '1.1s',
            opacity: 0.2,
          }}
        ></span>
        <span
          className="particle"
          style={{
            top: '95%',
            left: '77%',
            backgroundColor: '#d76ef0',
            width: '8px',
            height: '8px',
            animationDelay: '0.8s',
            opacity: 0.2,
          }}
        ></span>
      </div>

      <Container>
        <section>
          <h1 className="home-screen-heading">
            <span>
              {t('home-screen-heading-1')}
              <br />
              <span className="gradient-text">
                {t('home-screen-heading-2')}
              </span>
            </span>
          </h1>

          <Row>
            <Col xxl={2} xl={1} lg={0}></Col>
            <Col xxl={8} xl={10} lg={12}>
              <h2 className="home-screen-sub-heading">
                {t('home-screen-intro-1')}{' '}
                <span style={{ color: '#fe4a49', fontWeight: '550' }}>
                  {t('home-screen-intro-2')}
                </span>
              </h2>
            </Col>
            <Col xxl={2} xl={1} lg={0}></Col>
          </Row>

          <Row>
            <Col md={2}></Col>
            <Col md={8} className="home-screen-button-container">
              <Button
                variant="primary"
                className="hover-grow home-screen-customizer-button"
                onClick={() => {
                  navigate('/customizer');
                }}
              >
                {t('home-screen-open-customizer')}
                <svg viewBox="0 0 16 16" width="22px" fill="#ffffff">
                  <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                  <g
                    id="SVGRepo_tracerCarrier"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></g>
                  <g id="SVGRepo_iconCarrier">
                    <path
                      fill="#ffffff"
                      fillRule="evenodd"
                      d="M11.7071,4.29289 L15.4142,8 L11.7071,11.7071 C11.3166,12.0976 10.6834,12.0976 10.2929,11.7071 C9.90237,11.3166 9.90237,10.6834 10.2929,10.2929 L11.5858,9 L2,9 C1.44771,9 1,8.55228 1,8 C1,7.44772 1.44771,7 2,7 L11.5858,7 L10.2929,5.70711 C9.90237,5.31658 9.90237,4.68342 10.2929,4.29289 C10.6834,3.90237 11.3166,3.90237 11.7071,4.29289 Z"
                    ></path>
                  </g>
                </svg>
              </Button>
              <Button
                variant="light"
                className="hover-grow home-screen-community-button"
                onClick={() => {
                  navigate('/community-designs');
                }}
              >
                {t('home-screen-browse-community')}
              </Button>
            </Col>
            <Col md={2}></Col>
          </Row>

          <Row>
            <Col md={12} className="d-flex justify-content-center">
              <div className="home-screen-image-container">
                {/* Flow image to determine height of the home-screen-image-container (invisible and not absolutely positioned) */}
                <img
                  src={`/images/keycapSets/9.png`} // the tallest image
                  alt="Keycap Set"
                  className="image-sizing-helper"
                />

                {/* Current Image */}
                <img
                  key={`current-${currentImage}`}
                  src={`/images/keycapSets/${currentImage}.png`}
                  alt={`Keycap Set ${currentImage}`}
                  className={`fade-image ${
                    isTransitioning ? 'fade-out' : 'fade-in'
                  }`}
                />

                {/* Next Image - initially hidden, then fades in */}
                {isTransitioning && (
                  <img
                    key={`next-${imageQueue[nextIndex]}`}
                    src={`/images/keycapSets/${imageQueue[nextIndex]}.png`}
                    alt={`Keycap Set ${imageQueue[nextIndex]}`}
                    className={`fade-image ${fadeInNext ? 'fade-in' : ''}`}
                  />
                )}
              </div>
            </Col>
          </Row>
        </section>

        <section>
          <Row>
            <Col md={12}>
              <h2 className="why-custom-keycaps-heading">
                {t('home-screen-why-custom-keycaps')}
              </h2>
            </Col>
          </Row>

          <Row className="home-screen-card-container">
            <Col md={4} className="d-flex">
              <Card
                className="home-screen-card card-red"
                onClick={() => {
                  navigate('/customizer');
                }}
              >
                <div className="card-icon-wrapper">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#fe4a49"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="13.5" cy="6.5" r=".5" fill="#fe4a49"></circle>
                    <circle cx="17.5" cy="10.5" r=".5" fill="#fe4a49"></circle>
                    <circle cx="8.5" cy="7.5" r=".5" fill="#fe4a49"></circle>
                    <circle cx="6.5" cy="12.5" r=".5" fill="#fe4a49"></circle>
                    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"></path>
                  </svg>
                </div>
                <Card.Body>
                  <Card.Title
                    className="text-center card-title"
                    style={{ marginBottom: '20px' }}
                  >
                    {t('home-screen-powerful-tool')}
                  </Card.Title>
                  <Card.Text className="text-center">
                    {t('home-screen-powerful-tool-info')}
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4} className="d-flex">
              <Card
                className="home-screen-card card-blue"
                onClick={() => {
                  navigate('/community-designs');
                }}
              >
                <div className="card-icon-wrapper">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#3B82F6"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                </div>
                <Card.Body>
                  <Card.Title
                    className="text-center card-title"
                    style={{ marginBottom: '20px' }}
                  >
                    {t('home-screen-community-driven')}
                  </Card.Title>
                  <Card.Text className="text-center">
                    {t('home-screen-community-driven-info')}
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4} className="d-flex">
              <Card
                className="home-screen-card card-purple"
                onClick={() => {
                  navigate('/faq#faq-6');
                }}
              >
                <div className="card-icon-wrapper">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#a855f7"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="m15.477 12.89 1.515 8.526a.5.5 0 0 1-.81.47l-3.58-2.687a1 1 0 0 0-1.197 0l-3.586 2.686a.5.5 0 0 1-.81-.469l1.514-8.526"></path>
                    <circle cx="12" cy="8" r="6"></circle>
                  </svg>
                </div>
                <Card.Body>
                  <Card.Title
                    className="text-center card-title"
                    style={{ marginBottom: '20px' }}
                  >
                    {t('home-screen-german-quality')}
                  </Card.Title>
                  <Card.Text className="text-center">
                    {t('home-screen-german-quality-info')}
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </section>

        <section className="home-screen-community-designs">
          <Row>
            <Col md={12} className="text-center" style={{ height: 'auto' }}>
              <h2>{t('home-screen-community-designs')}</h2>

              <p className="subheading">
                {t('home-screen-community-designs-info')}
              </p>

              {/* Scrollable strip */}
              <div ref={scrollRef} className="top-designs-container">
                {topDesigns.map((d) => (
                  <Card
                    key={d._id}
                    className="top-design-card"
                    onClick={() => {
                      navigate('/community-designs');
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-top">
                      <p className="design-info">
                        {d.name}
                        <br />
                        <span className="by">
                          {t('home-screen-by')} {d.metadata.username}
                        </span>
                      </p>
                      <Card.Text className="design-likes">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width={'19'}
                          height={'19'}
                          fill="#fe4a49"
                          className="bi bi-heart-fill"
                          viewBox="0 0 16 16"
                          style={{
                            marginBottom: '2px',
                          }}
                        >
                          <path
                            fillRule="evenodd"
                            d="M8 1.314C12.438-3.248 23.534 4.735 8 15-7.534 4.736 3.562-3.248 8 1.314"
                          />
                        </svg>
                        <span
                          className="no-wrap mx-2"
                          style={{ color: '#fe4a49' }}
                        >
                          {d.metadata.numOfLikes}
                        </span>
                      </Card.Text>
                    </div>
                    <Card.Img src={d.metadata.thumbnail} />
                  </Card>
                ))}
              </div>

              <Button
                variant="light"
                className="explore-designs-button hover-grow"
                onClick={() => {
                  navigate('/community-designs');
                }}
              >
                {t('home-screen-explore-designs')}
              </Button>
            </Col>
          </Row>
        </section>
      </Container>
    </div>
  );
}

export default HomeScreen;
